import React from "react";
import { useTheme } from "@mui/material/styles";
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  useMediaQuery,
  Button,
  CircularProgress,
  Typography,
  Alert,
  Link,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const BRAND = "#f05d32";
const BRAND_HOVER = "#d94e27";
const MUTED = "#6b6b65";

const MetaRow = ({ icon, children }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: 1.25,
      color: MUTED,
      fontSize: 15,
      lineHeight: 1.45,
    }}
  >
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: "50%",
        bgcolor: "rgba(240, 93, 50, 0.1)",
        color: BRAND,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        fontSize: 14,
      }}
    >
      <i className={icon} />
    </Box>
    <Box sx={{ pt: 0.5, minWidth: 0 }}>{children}</Box>
  </Box>
);

const MyDialogTitle = (props) => {
  const { children, onClose, ...other } = props;
  return (
    <DialogTitle
      sx={{
        m: 0,
        px: 3,
        py: 2,
        backgroundColor: BRAND,
        color: "#fff",
        display: "flex",
        alignItems: "center",
        position: "relative",
        fontWeight: 600,
        fontSize: { xs: 18, sm: 20 },
        letterSpacing: "0.01em",
      }}
      {...other}
    >
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translate(0,-50%)",
            color: "#fff",
            "&:hover": {
              bgcolor: "rgba(255,255,255,0.15)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
};

const SpotCard = ({ component, slug, url, phone, onOrder }) => (
  <Box
    sx={{
      width: "100%",
      bgcolor: "#fff",
      border: "1px solid rgba(0,0,0,0.06)",
      borderRadius: "16px",
      p: 2,
      transition: "border-color 0.2s ease, box-shadow 0.2s ease",
      "&:hover": {
        borderColor: "rgba(240, 93, 50, 0.35)",
        boxShadow: "0 4px 18px rgba(240, 93, 50, 0.08)",
      },
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.75,
        mb: 2,
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          bgcolor: "rgba(240, 93, 50, 0.1)",
          color: BRAND,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 22,
          flexShrink: 0,
        }}
      >
        <i className="fa-light fa-store" />
      </Box>
      <Box sx={{ minWidth: 0, textAlign: "left" }}>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: BRAND,
            mb: 0.35,
            lineHeight: 1.2,
          }}
        >
          {component.ucfirst("store")}
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: 20, sm: 22 },
            fontWeight: 700,
            lineHeight: 1.2,
            color: "#1a1a1a",
          }}
        >
          {component.trans(`${slug}-name`)}
        </Typography>
      </Box>
    </Box>

    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        mb: 2.5,
      }}
    >
      <MetaRow icon="fa-light fa-location-dot">
        <Link
          href={url}
          rel="noreferrer"
          target="_blank"
          underline="hover"
          sx={{ color: MUTED }}
        >
          {component.trans(`${slug}-address`)}
        </Link>
      </MetaRow>
      <MetaRow icon="fa-light fa-phone">
        <Link href={"tel:" + phone} underline="hover" sx={{ color: MUTED }}>
          {phone}
        </Link>
      </MetaRow>
      <MetaRow icon="fa-light fa-clock">
        <span>{component.trans(`${slug}-hours`)}</span>
      </MetaRow>
    </Box>

    <Button
      onClick={onOrder}
      fullWidth
      sx={{
        bgcolor: BRAND,
        borderRadius: "50px",
        border: `1px solid ${BRAND}`,
        color: "#fff",
        fontSize: 15,
        fontWeight: 600,
        py: 1.25,
        px: 2,
        textTransform: "none",
        boxShadow: "0 2px 8px rgba(240, 93, 50, 0.25)",
        transition: "background 0.2s ease, box-shadow 0.2s ease",
        "&:hover": {
          bgcolor: BRAND_HOVER,
          borderColor: BRAND_HOVER,
          boxShadow: "0 4px 14px rgba(240, 93, 50, 0.35)",
        },
      }}
    >
      {component.ucfirst("order-here")}
    </Button>
  </Box>
);

export default function DeliveryType(props) {
  const { component } = props;
  const devHelper = component.getHelpers("dev");

  const { open = false, spots = null } = component.getData("default", {});

  const fullScreen = useMediaQuery(useTheme().breakpoints.down("sm"));
  const singleSpot = spots?.length === 1;

  return (
    <Dialog
      onClose={() => {
        component.setData({ "default.open": false });
      }}
      open={open}
      fullScreen={fullScreen}
      sx={{
        "& .MuiPaper-root": {
          borderRadius: { xs: 0, sm: "20px" },
          minWidth: { xs: "100%", sm: 440 },
          maxWidth: { sm: 480 },
          overflow: "hidden",
        },
      }}
    >
      <MyDialogTitle
        onClose={() => {
          component.setData({ "default.open": false });
        }}
      >
        {component.ucfirst(
          singleSpot ? "takeaway" : "select-spot-dialog-title",
        )}
      </MyDialogTitle>

      <DialogContent
        dividers
        sx={{
          p: { xs: 2, sm: 2.5 },
          bgcolor: "#fafafa",
          borderColor: "rgba(0,0,0,0.06)",
        }}
      >
        {!spots ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
            }}
          >
            <CircularProgress size={36} sx={{ color: BRAND }} />
          </Box>
        ) : spots.length === 0 ? (
          <Box
            sx={{
              minHeight: 120,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Alert
              severity="info"
              sx={{
                width: "100%",
                borderRadius: "12px",
                alignItems: "center",
              }}
            >
              {component.ucfirst("zero-spots")}
            </Alert>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            {spots.map((spot) => {
              const { slug, url, phone } = devHelper.getObjectValue(
                spot,
                "_values",
              );
              return (
                <SpotCard
                  key={slug}
                  component={component}
                  slug={slug}
                  url={url}
                  phone={phone}
                  onOrder={() =>
                    component.updateSpot(devHelper.getObjectValue(spot, "_id"))
                  }
                />
              );
            })}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
